// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Route, ClassId } from '../types';
import { esc, num, pct, money, flag, shortName, fmtValue, countryColour } from '../format';
import { sparkline } from '../charts';
import { navigate } from '../main';
import { classCountries } from '../analysis';

export function renderCountries(root: HTMLElement, d: Dataset, _route: Route): void {
  const order = d.meta.classOrder;
  const store = 'au-foreign-owned:lb-class';
  let cid: ClassId = (localStorage.getItem(store) as ClassId) || 'agricultural';
  if (!order.includes(cid)) cid = 'agricultural';

  function draw(): void {
    const list = classCountries(d, cid).filter((c) => (c.value ?? 0) > 0);
    const max = list.length ? (list[0].value ?? 1) : 1;
    const meta = d.meta.classMeta[cid];
    const rows = list.map((c, i) => {
      const col = countryColour(c.iso, c.name);
      const w = Math.max(1.5, ((c.value ?? 0) / max) * 100);
      const valStr = cid === 'commercial' && c.dollars != null ? money(c.dollars) : fmtValue(cid, c.value);
      const spark = cid === 'agricultural' && c.series.filter((v) => v != null).length > 1
        ? sparkline(c.series, col, 90, 22) : '';
      const chg = c.changePct != null ? `<span class="${c.changePct >= 0 ? 'up' : 'down'}" style="font-size:var(--font-size-xs)"> ${c.changePct >= 0 ? '+' : ''}${(c.changePct * 100).toFixed(0)}%</span>` : '';
      const tip = `${c.name} — ${meta.label}\n${valStr} · ${pct(c.share)} of foreign-held · rank #${i + 1}`;
      return `
        <div class="barrow" data-country="${esc(c.name)}" data-tip="${esc(tip)}">
          <span class="lbl"><span class="rank" style="margin-right:6px">${i + 1}</span><span class="cdot" style="background:${col}"></span><span class="flagemoji">${flag(c.iso)}</span><span style="min-width:0;overflow:hidden;text-overflow:ellipsis">${esc(shortName(c.name))}</span></span>
          <span class="track"><span style="width:${w}%;background:${col}"></span></span>
          <span class="val">${valStr}${chg}${spark ? `<span style="margin-left:8px;vertical-align:middle">${spark}</span>` : ''}</span>
        </div>`;
    }).join('');

    const note = cid === 'commercial'
      ? `Ranked by value ($) of foreign-registered commercial land; the count of sites is shown in each country's profile.`
      : cid === 'residential'
      ? `Ranked by number of residential acquisitions in the latest year (a yearly flow, not a running total).`
      : `Ranked by ${meta.unitLabel}.`;

    root.querySelector('.lb-body')!.innerHTML = `
      <div class="panel-head"><h3>${esc(meta.label)} — ${list.length} source countries</h3><p>${note} Bar width is share of the foreign-held total. Click a country for its full cross-asset profile.${cid === 'agricultural' ? ' Sparkline shows 2017–' + String(d.meta.latestYear).slice(2) + '.' : ''}</p></div>
      <div class="barlist">${rows || '<p class="empty-state">No data.</p>'}</div>`;
    root.querySelectorAll<HTMLElement>('.barrow[data-country]').forEach((el) => el.addEventListener('click', () => navigate({ country: el.dataset.country })));
  }

  root.innerHTML = `
    <div class="view-head">
      <h2>Country leaderboard</h2>
      <p>Rank every source country within a single asset class. Switch class and the order reshuffles completely — the leader of farmland is nowhere near the top of commercial property or business interests.</p>
    </div>
    <div class="controls">
      <span class="control-label">Asset class</span>
      <div class="seg">${order.map((c) => `<button data-cls="${c}" aria-pressed="${c === cid}">${esc(d.meta.classMeta[c].short)}</button>`).join('')}</div>
    </div>
    <div class="panel"><div class="lb-body"></div></div>
  `;
  root.querySelectorAll<HTMLElement>('[data-cls]').forEach((b) => b.addEventListener('click', () => {
    cid = b.dataset.cls as ClassId; localStorage.setItem(store, cid);
    root.querySelectorAll<HTMLElement>('[data-cls]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    draw();
  }));
  void num;
  draw();
}
